import type { TranslatedString } from '@interfaces/shared';
import type { Holding } from '@interfaces/stats';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { useAccordionTokens } from '@ovComponents/2-component/accordion/accordian.tokens';
import { currencySymbol } from '@utils/currency';
import {
  formatMoneyValue,
  formatPercentPoints,
  formatPercentValue,
} from '@utils/moneyFormatting';
import { useTheme } from 'hephaestus/web';
import { round } from 'lodash-es';
import type { TFunction } from 'react-i18next';
import {
  find1Day,
  find1Month,
  find1Week,
  find3Day,
} from './components/tableHoldings/utils';

export type HoldingAttributes = {
  key: string;
  hexColor: string;
  secondaryAssetClassName: string;
  secondaryAssetClassDescription: string;
  percentage: number;
  totalCents?: number;
};
export const getAssetBarIndeterminateColor = (index: number): string => {
  const arrayOfAshColors = [
    '#EEF1F4',
    '#E5E8EB',
    '#DBDEE0',
    '#D1D4D6',
    '#C7CACC',
  ];
  return arrayOfAshColors[index];
};

export type FinancialProduct = {
  id: number;
  name: number;
  ticker: string;
  translatedName: TranslatedString;
  translatedPortfolioDescription: TranslatedString;
};
export type SecurityHoldings = {
  percentage: number;
  totalCents: number;
  quantity: number;
  financialProduct: FinancialProduct;
};
export type SecondaryAssetClassHoldings = {
  assetClass: AssetClass;
  percentage: number;
  totalCents: number;
  securityHoldings: SecurityHoldings[];
};
export type PrimaryAssetClassHoldings = {
  assetClass: AssetClass;
  percentage: number;
  totalCents: number;
  secondaryAssetClassHoldings: SecondaryAssetClassHoldings[];
};
export type AssetClass = {
  key: string;
  translatedName: TranslatedString;
  translatedDescription: TranslatedString;
};

export type HoldingResponse = {
  financialProduct: {
    id: number;
    name: number;
    ticker: string;
    translatedName: TranslatedString;
    translatedPortfolioDescription: TranslatedString;
    primaryAssetClass: AssetClass;
    secondaryAssetClass: AssetClass;
  };
  percentage?: number;
  totalCents?: number;
  quantity?: number;
  originalCurrency?: {
    currency: CurrencyCodes;
    totalCents: number;
  };
  userBookValue?: number;
  goalBookValue?: number;
  accountBookValue?: number;
  subAccountBookValue?: number;
  householdClientGroupBookValue?: number;
};

export const useSupportColors = () => {
  const theme = useTheme();
  const tokens = useAccordionTokens(theme);

  return [
    {
      active: tokens.progressBar.supportColors.activeSupportColorOne,
      inactive: tokens.progressBar.supportColors.inactiveSupportColorOne,
    },
    {
      active: tokens.progressBar.supportColors.activeSupportColorTwo,
      inactive: tokens.progressBar.supportColors.inactiveSupportColorTwo,
    },
    {
      active: tokens.progressBar.supportColors.activeSupportColorThree,
      inactive: tokens.progressBar.supportColors.inactiveSupportColorThree,
    },
    {
      active: tokens.progressBar.supportColors.activeSupportColorFour,
      inactive: tokens.progressBar.supportColors.inactiveSupportColorFour,
    },
    {
      active: tokens.progressBar.supportColors.activeSupportColorFive,
      inactive: tokens.progressBar.supportColors.inactiveSupportColorFive,
    },
    {
      active: tokens.progressBar.supportColors.activeSupportColorSix,
      inactive: tokens.progressBar.supportColors.inactiveSupportColorSix,
    },
  ];
};

export const formatAllocationPercentage = (
  percentage: number | undefined,
  showTwoDecimalPlaces: boolean,
) => {
  if (showTwoDecimalPlaces) return formatPercentPoints(percentage ?? 0);
  // Math.round(-0.3) is -0, which Intl prints as "-0%"
  return formatPercentValue((Math.round(percentage ?? 0) || 0) / 100, 0);
};

export const calculatePrimaryAssetClassHoldings = (
  holdings: any[],
  totalCents: number,
): any[] => {
  const primaryAssetClassHoldings = holdings.reduce(
    (result: any[], current: HoldingResponse) => {
      if (
        !current.financialProduct.primaryAssetClass ||
        !current.financialProduct.secondaryAssetClass
      ) {
        // Skipping if primaryAssetClass or secondaryAssetClass is null
        return result;
      }
      const index = result.findIndex(
        (a) =>
          a.assetClass.key === current.financialProduct.primaryAssetClass.key,
      );
      if (index === -1) {
        result.push({
          assetClass: { ...current.financialProduct.primaryAssetClass },
          totalCents:
            current?.originalCurrency?.totalCents ?? current.totalCents ?? 0,
          secondaryAssetClassHoldings: [
            {
              assetClass: { ...current.financialProduct.secondaryAssetClass },
              totalCents:
                current?.originalCurrency?.totalCents ??
                current.totalCents ??
                0,
              securityHoldings: [
                {
                  financialProduct: { ...current.financialProduct },
                  totalCents:
                    current?.originalCurrency?.totalCents ??
                    current.totalCents ??
                    0,
                  quantity: current.quantity ?? 0,
                },
              ],
            },
          ],
        });
      } else {
        const { secondaryAssetClassHoldings } = result[index];
        const assetClassIndex = secondaryAssetClassHoldings.findIndex(
          (a: SecondaryAssetClassHoldings) =>
            a.assetClass.key ===
            current.financialProduct.secondaryAssetClass.key,
        );
        if (assetClassIndex === -1) {
          secondaryAssetClassHoldings.push({
            assetClass: { ...current.financialProduct.secondaryAssetClass },
            totalCents:
              current?.originalCurrency?.totalCents ?? current.totalCents ?? 0,
            securityHoldings: [
              {
                financialProduct: { ...current.financialProduct },
                totalCents:
                  current?.originalCurrency?.totalCents ??
                  current.totalCents ??
                  0,
                quantity: current.quantity ?? 0,
              },
            ],
          });
        } else {
          const { securityHoldings } =
            secondaryAssetClassHoldings[assetClassIndex];
          const securityHoldingsIndex = securityHoldings.findIndex(
            (a: SecurityHoldings) =>
              a.financialProduct.id === current.financialProduct.id,
          );
          if (securityHoldingsIndex === -1) {
            securityHoldings.push({
              financialProduct: { ...current.financialProduct },
              totalCents:
                current?.originalCurrency?.totalCents ??
                current.totalCents ??
                0,
              quantity: current.quantity ?? 0,
            });
          } else {
            securityHoldings[securityHoldingsIndex] = {
              ...securityHoldings[securityHoldingsIndex],
              totalCents:
                (current?.originalCurrency?.totalCents ??
                  current.totalCents ??
                  0) + securityHoldings[securityHoldingsIndex].totalCents,
              quantity:
                (current.quantity ?? 0) +
                securityHoldings[securityHoldingsIndex].quantity,
            };
          }

          secondaryAssetClassHoldings[assetClassIndex] = {
            ...secondaryAssetClassHoldings[assetClassIndex],
            totalCents:
              (current?.originalCurrency?.totalCents ??
                current.totalCents ??
                0) + secondaryAssetClassHoldings[assetClassIndex].totalCents,
            securityHoldings,
          };
        }
        result[index] = {
          ...result[index],
          totalCents:
            (current?.originalCurrency?.totalCents ?? current.totalCents ?? 0) +
            result[index].totalCents,
          secondaryAssetClassHoldings,
        };
      }

      return result.map((r: any) => ({
        ...r,
        percentage: (r.totalCents / totalCents) * 100,
        secondaryAssetClassHoldings: r.secondaryAssetClassHoldings.map(
          (s: any) => ({
            ...s,
            percentage: (s.totalCents / totalCents) * 100,
            securityHoldings: s.securityHoldings.map((h: any) => ({
              ...h,
              percentage: (h.totalCents / totalCents) * 100,
            })),
          }),
        ),
      }));
    },
    [],
  );

  return primaryAssetClassHoldings;
};

export const oneDayDifferenceCents = (holding: Holding, history: any[]) => {
  const currentTotal = holding.totalCents ?? 0;
  const historicalTotal = find1Day(holding, history)?.totalCents;
  const eodTotal = (holding.eodPriceCents ?? 0) * (holding.quantity ?? 0);

  const difference = round(currentTotal - (historicalTotal ?? eodTotal ?? 0));

  return difference;
};

export const threeDayDifferenceCents = (holding: Holding, history: any[]) =>
  (holding.totalCents ?? 0) - (find3Day(holding, history)?.totalCents ?? 0);

export const oneWeekDifferenceCents = (holding: Holding, history: any[]) =>
  (holding.totalCents ?? 0) - (find1Week(holding, history)?.totalCents ?? 0);

export const oneMonthDifferenceCents = (holding: Holding, history: any[]) =>
  (holding.totalCents ?? 0) - (find1Month(holding, history)?.totalCents ?? 0);

export const getUnrealizedGainsAndLoss = (
  holding: Holding,
  unrealizedGainsAndLossCents: number,
  unrealizedGainsAndLossPercent: number,
) => (
  <>
    {`${formatMoneyValue(unrealizedGainsAndLossCents, currencySymbol[(holding.currency as CurrencyCodes) ?? CurrencyCodes.CAD] ?? '$', 2, false)}
    (${formatPercentValue(unrealizedGainsAndLossPercent)})`}
  </>
);

export const getOneDayChange = (holding: Holding, history: any[]) => {
  const oneDayDiff = oneDayDifferenceCents(holding, history);
  const eodPrice = holding.eodPriceCents;
  const { quantity } = holding;
  const historyTotal = find1Day(holding, history)?.totalCents;
  const denominator = historyTotal ?? (eodPrice ?? 0) * (quantity ?? 0);

  return (
    <>
      {`${formatMoneyValue(oneDayDifferenceCents(holding, history), currencySymbol[(holding.currency as CurrencyCodes) ?? CurrencyCodes.CAD] ?? '$', 2, false)} `}
      {historyTotal === undefined && !eodPrice
        ? '(-)'
        : denominator !== 0
          ? `(${formatPercentValue(oneDayDiff / denominator)})`
          : '(-)'}
    </>
  );
};

export const getThreeDayChange = (holding: Holding, history: any[]) => {
  const threeDayDiff = threeDayDifferenceCents(holding, history);
  return (
    <>
      {`${formatMoneyValue(threeDayDiff, currencySymbol[(holding.currency as CurrencyCodes) ?? CurrencyCodes.CAD] ?? '$', 2, false)} `}
      {find3Day(holding, history)?.totalCents
        ? `(${formatPercentValue(threeDayDiff / (find3Day(holding, history)?.totalCents ?? 0))})`
        : '-'}
    </>
  );
};

export const getOneWeekChange = (holding: Holding, history: any[]) => {
  const oneWeekDiff = oneWeekDifferenceCents(holding, history);
  return (
    <>
      {`${formatMoneyValue(oneWeekDiff, currencySymbol[(holding.currency as CurrencyCodes) ?? CurrencyCodes.CAD] ?? '$', 2, false)} `}
      {find1Week(holding, history)?.totalCents
        ? `(${formatPercentValue(oneWeekDiff / (find1Week(holding, history)?.totalCents ?? 0))})`
        : '-'}
    </>
  );
};

export const getOneMonthChange = (holding: Holding, history: any[]) => {
  const oneMonthDiff = oneMonthDifferenceCents(holding, history);
  return (
    <>
      {`${formatMoneyValue(oneMonthDiff, currencySymbol[(holding.currency as CurrencyCodes) ?? CurrencyCodes.CAD] ?? '$', 2, false)} `}
      {find1Month(holding, history)?.totalCents
        ? `(${formatPercentValue(oneMonthDiff / (find1Month(holding, history)?.totalCents ?? 0))})`
        : '-'}
    </>
  );
};

export const getSecurityType = (value: string | undefined, t: TFunction) => {
  const allTypes = t('configureModels:productModal.allTypes', {
    returnObjects: true,
  }) as Record<string, string>;
  if (value && allTypes[value]) {
    return allTypes[value];
  }
  return '-';
};
